// The Hasura session a server sets for a verified caller when it queries with
// the admin secret. In the target model Hasura never sees a Connected App
// token: Hasura does not check scopes, so a token approved only for records
// would get the person's full access. A server verifies the token, checks its
// scopes, and calls Hasura itself with these headers. Hasura's role and row
// rules still apply.
//
// A person's agent (a Connected App token: sub is the member) runs under the
// person's own role; its scopes, checked by the server, limit what it may do.
// Only a machine client that acts for an organization rather than a person (a
// Stytch M2M token: sub is the client itself) runs under its audience's agent
// role, which keeps it out of the areas of the database that role leaves out.
// An audience with no agent role gets no session at all.
import type { Principal } from "./stytch-verify.js"

/** The Heavybit organization's record id: every staff member's x-hasura-org-id. */
export const HEAVYBIT_ORG_ID = 23915

/** The agent role per audience (the role-name prefix before the first "_"). */
export const AGENT_ROLES: Readonly<Record<string, string>> = {
  heavybit: "heavybit_agent",
  portfolio: "portfolio_agent",
}

type SessionPrincipal = Pick<Principal, "userId" | "defaultRole" | "clientId" | "agentId" | "personId" | "orgId"> &
  Partial<Pick<Principal, "sub">>

/** True for a machine client: an M2M token names the client, not a member, as its subject. */
export function isMachineClient(principal: SessionPrincipal): boolean {
  return principal.clientId !== undefined && principal.sub === principal.clientId
}

/** The Hasura role for this caller, or null when a machine client has no role to run under. */
export function hasuraRoleFor(principal: SessionPrincipal): string | null {
  if (!isMachineClient(principal)) return principal.defaultRole
  const audience = principal.defaultRole.split("_")[0] ?? ""
  return AGENT_ROLES[audience] ?? null
}

/**
 * Session headers for an admin-secret request on the caller's behalf, or null
 * when the caller may not have one. The caller adds x-hasura-admin-secret.
 * The Network organization is sent as "0": an org-scoped rule then matches
 * nothing. A missing org id gets no session at all. principalFromClaims
 * already reads the Network's "" and "0" as 0, so undefined means the claim
 * was absent or not an id, and guessing the Network for it would hide a
 * broken token behind a valid-looking session.
 *
 * A member (a person, or a person's agent) with no person id gets no session
 * either: the owner and per-person rules compare X-Hasura-Person-Id, and
 * Hasura fails every such query when the variable is absent. Sign-in already
 * refuses a person with no person record, so this is a broken token. Only a
 * machine client has no person: it acts for an organization and owns nothing.
 */
export function hasuraSessionHeaders(principal: SessionPrincipal): Record<string, string> | null {
  const role = hasuraRoleFor(principal)
  if (!role || principal.orgId === undefined) return null
  if (!isMachineClient(principal) && principal.personId === undefined) return null
  const headers: Record<string, string> = {
    "x-hasura-role": role,
    "x-hasura-user-id": principal.userId,
    "x-hasura-org-id": String(principal.orgId),
  }
  if (principal.personId !== undefined) headers["x-hasura-person-id"] = String(principal.personId)
  if (principal.agentId) headers["x-hasura-agent-id"] = principal.agentId
  return headers
}

/**
 * The session for a local-dev admin-secret client with sign-in turned off
 * (VITE_FORCE_USER, AUGER_FORCE_USER, FUNCTIONS_FORCE_USER). The forced user
 * is staff, so the org is Heavybit's. personId is that user's person record
 * (users.record_id). Without it the header is left out, and Hasura refuses
 * every query that reaches an owner rule with "missing session variable".
 */
export function devStaffSessionHeaders(userId: string, personId?: string): Record<string, string> {
  const headers: Record<string, string> = {
    "x-hasura-role": "heavybit_staff",
    "x-hasura-user-id": userId,
    "x-hasura-org-id": String(HEAVYBIT_ORG_ID),
  }
  if (personId) headers["x-hasura-person-id"] = personId
  return headers
}
